import { Box } from '@mui/system';
import { FieldTemplate, LayoutItem } from 'api/models';
import { FieldType } from 'api/models/FieldTemplate';
import { useDrop } from 'react-dnd';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { dialogState, fieldTemplatesState, layoutSelector } from 'state';
import { push } from 'object-path';
import { produce } from 'immer';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  getFieldsOfSelectableTable,
  getNewFieldItem,
  getNewInputLayoutItem,
} from 'routes/FormTemplateEditor/FormEditor/utils';
import { getId } from 'utils';
import { DialogTypes } from 'types/types';
import SelectableTableField from './SelectableTableField';
import { get, del } from 'object-path';

interface SelectableTableProps {
  parentPath: string;
  index: number;
  disabled?: boolean;
}

export default function SelectableTable({
  parentPath,
  index,
  disabled,
}: SelectableTableProps) {
  const path = `${parentPath}.${index}`;
  const openDialog = useSetRecoilState(dialogState);
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const [fieldTemplates, setFieldTemplates] =
    useRecoilState(fieldTemplatesState);
  const layoutItem = get(layout, path);
  const { t } = useTranslation();

  const selects = getFieldsOfSelectableTable({
    layoutItems: layoutItem?.children,
    fieldTemplates,
  });

  const handleDrop = (el: {
    layoutItem: LayoutItem;
    field?: FieldTemplate;
    fromPath?: string;
  }) => {
    const items = produce(layout, draft => {
      push(draft, `${path}.children`, el.layoutItem);
      // Remove the item from the previous location
      if (el.fromPath) {
        del(draft, el.fromPath);
      }
    });
    setLayout(items);
  };

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: 'field-template',
      collect: monitor => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
      }),
      drop: handleDrop,
      canDrop: handleCanDrop,
    }),
    [layout],
  );

  function handleCanDrop(el: {
    layoutItem: LayoutItem;
    field?: FieldTemplate;
  }) {
    return el.field?.fieldType === FieldType.Select;
  }

  function onDelete(index: number) {
    const itemToBeDeleted = get(layout, `${path}.children.${index}`);
    const items = produce(layout, draft => {
      get(draft, `${path}.children`)?.splice(index, 1);
    });
    const templates = fieldTemplates.filter(
      field => field.id !== itemToBeDeleted?.fieldTemplateId,
    );
    setFieldTemplates(templates);
    setLayout(items);
  }

  function onAddSelect() {
    const layoutItemId = getId();
    const fieldItemId = getId();
    const newLayoutItem = getNewInputLayoutItem({
      id: layoutItemId,
      fieldTemplateId: fieldItemId,
    });
    const newField = getNewFieldItem(
      FieldType.Select,
      fieldItemId,
      layoutItemId,
    );
    setFieldTemplates([...fieldTemplates, newField]);

    const childrenLength = get(layout, `${path}.children`).length;

    const itemPath = `${path}.children.${childrenLength}`;

    openDialog({
      isOpen: true,
      dialogType: DialogTypes.addFieldDialog,
      data: {
        fieldTemplate: newField,
        layoutItem: newLayoutItem,
        path: itemPath,
      },
    });
    const items = produce(layout, draft =>
      push(draft, `${path}.children`, newLayoutItem),
    );
    setLayout(items);
  }

  function onEdit(field?: FieldTemplate) {
    openDialog({
      isOpen: true,
      dialogType: DialogTypes.addFieldDialog,
      data: {
        fieldTemplate: field,
        layoutItem: layoutItem,
        path,
      },
    });
  }

  return (
    <Box
      position={'relative'}
      minHeight={'200px'}
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'center'}
    >
      {canDrop && (
        <Box
          ref={drop}
          width="100%"
          height="100%"
          position={'absolute'}
          top={0}
          sx={{
            background: theme => theme.palette.background.paper,
            opacity: canDrop ? 1 : 0,
            zIndex: 100,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '20px',
            border: theme =>
              isOver ? `2px dashed ${theme.palette.divider}` : 'none',
            transition: 'border 0.3s ease',
          }}
        >
          {t('forms.addSelectToSelectableTable')}
        </Box>
      )}
      {(layoutItem?.children?.length ?? 0) > 0 && (
        <SelectableTableField
          onDelete={onDelete}
          onEdit={onEdit}
          fields={selects}
          path={path}
          children={layoutItem?.children}
          disabled={disabled}
          externalFieldValues={{}}
        />
      )}
      <Box display={'flex'}>
        <Button onClick={onAddSelect} sx={{ mt: 2 }} disabled={disabled}>
          <Add />
          {t('forms.addSelectToSelectableTable')}
        </Button>
      </Box>
    </Box>
  );
}
